import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useGql } from '@ah/design';
import { Header, PillTabs, PullToRefresh, ResizedImage, type PillTab } from '@ah/design/mobile';
import { Heart, Sparkles } from 'lucide-react';
import {
  MY_CIRCLES_BY_TYPE_QUERY,
  circleDisplayName,
  type HumanCircle,
} from '../shared/index.ts';
import { CircleListSkeleton, ErrorRetry } from './_parts.tsx';

const TABS: PillTab[] = [
  { to: '/coalitions', label: 'My Coalitions', end: true },
  { to: '/coalitions/suggested', label: 'Suggested' },
];

/**
 * Cause Coalitions the current user has joined. Mirrors the Circles list
 * structure (My / Suggested tabs) — see Circles.tsx for the reference
 * pattern. Discovery / first-join still happens via the home feed.
 */
export function Coalitions() {
  const gql = useGql();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['coalitions', 'my'],
    queryFn: async () => {
      const res = await gql.request<{ myCircles: HumanCircle[] }>(
        MY_CIRCLES_BY_TYPE_QUERY,
        { type: 'coalition' },
      );
      return res.myCircles;
    },
  });

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header title="Coalitions" />
      <PillTabs tabs={TABS} />
      <PullToRefresh className="flex-1 px-3 py-3" onRefresh={() => refetch()}>
        {isLoading ? (
          <CircleListSkeleton />
        ) : error ? (
          <ErrorRetry label="Could not load your coalitions." onRetry={() => refetch()} />
        ) : !data || data.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Heart size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm font-medium">You haven't joined any Coalitions yet.</p>
            <Link
              to="/coalitions/suggested"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-medium"
            >
              <Sparkles size={14} /> Browse Suggested
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {data.map((hc) => (
              <CoalitionRow key={hc.id} hc={hc} />
            ))}
          </div>
        )}
      </PullToRefresh>
    </div>
  );
}

function CoalitionRow({ hc }: { hc: HumanCircle }) {
  const { circle } = hc;
  const cover = circle.coverUrl;
  return (
    <Link
      to={`/circles/${circle.id}`}
      className="flex gap-3 items-center p-3 bg-card rounded-xl border border-border active:bg-accent"
    >
      <div className="w-14 h-14 shrink-0 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
        {cover ? (
          <ResizedImage src={cover} renderWidth={56} fallback={false} alt="" className="w-full h-full object-cover" />
        ) : (
          <Heart size={20} className="text-muted-foreground/60" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <h2 className="text-sm font-semibold truncate">
          {circleDisplayName(circle.displayTitle ?? circle.name)}
        </h2>
        {circle.problemHook ? (
          <p className="text-[12px] text-muted-foreground truncate mt-0.5 italic">
            “{circle.problemHook}”
          </p>
        ) : circle.description ? (
          <p className="text-[12px] text-muted-foreground truncate mt-0.5">
            {circle.description}
          </p>
        ) : null}
      </div>
    </Link>
  );
}
